import { useMemo } from 'react';
import { Typography, Card, Row, Col, Spin, Tag, Collapse, Table } from 'antd';
import { AppstoreAddOutlined, ShoppingCartOutlined, BarChartOutlined, TrophyOutlined, TableOutlined } from '@ant-design/icons';
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Bar, Cell } from 'recharts';
import './marketplace-products.css';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const MarketplaceProductsAnalysis = ({ marketplaceProductsData, loading, currentDateInfo }) => {
  const metrics = useMemo(() => {
    if (!marketplaceProductsData) {
      return {
        totalProducts: 0,
        productsByCategory: [],
        topCategory: null
      };
    }

    const totalProducts = marketplaceProductsData.total_products || 0;
    const productsByCategory = marketplaceProductsData.products_by_category || [];
    const topCategory = productsByCategory.length > 0
      ? productsByCategory.reduce((prev, current) =>
        (prev.count > current.count) ? prev : current
      )
      : null;

    return {
      totalProducts,
      productsByCategory,
      topCategory
    };
  }, [marketplaceProductsData]);

  const truncateText = (text, maxLength = 15) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const barChartData = useMemo(() => {
    return metrics.productsByCategory.slice(0, 10).map((category, index) => ({
      name: category.category || `Categoría ${index + 1}`,
      fullName: category.category || `Categoría ${index + 1}`,
      value: category.count || 0,
      color: index === 0 ? '#1890ff' :
        index === 1 ? '#52c41a' :
          index === 2 ? '#fa8c16' :
            index === 3 ? '#722ed1' :
              index === 4 ? '#eb2f96' :
                '#8c8c8c'
    }));
  }, [metrics.productsByCategory]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{data.fullName}</p>
          <p style={{ margin: '5px 0 0 0', color: '#666' }}>
            Productos: <strong>{data.value}</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card className="marketplace-products-card">
        <div className="card-header">
          <div className="card-icon card-icon-marketplace-products">
            <AppstoreAddOutlined />
          </div>
          <div>
            <Title level={3}>Productos del Marketplace</Title>
            <Text type="secondary">Inventario por categoría</Text>
          </div>
        </div>
        <div className="card-content">
          <Row gutter={[16, 16]}>
            {[1, 2, 3, 4].map((_, index) => (
              <Col span={6} key={index}>
                <div className="marketplace-metric-skeleton">
                  <Spin size="small" />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Card>
    );
  }

  return (
    <Card className="marketplace-products-card">
      <div className="card-header">
        <div className="card-icon card-icon-marketplace-products">
          <AppstoreAddOutlined />
        </div>
        <div>
          <Title level={3}>Productos del Marketplace</Title>
          <Text type="secondary">{currentDateInfo || `Inventario por categoría - Total: ${metrics.totalProducts} productos`}</Text>
        </div>
      </div>
      <div className="card-content">
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-products-total">
                <ShoppingCartOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {metrics.totalProducts}
                </div>
                <div className="metric-title">Total Productos</div>
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-categories">
                <BarChartOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {metrics.productsByCategory.length}
                </div>
                <div className="metric-title">Categorías</div>
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-top-category">
                <TrophyOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {metrics.topCategory ? (
                    <>
                      <div className="category-name" title={metrics.topCategory.category}>
                        {truncateText(metrics.topCategory.category, 15)}
                      </div>
                      <div className="category-count">
                        {metrics.topCategory.count} productos
                      </div>
                    </>
                  ) : (
                    <div className="no-data">Sin datos</div>
                  )}
                </div>
                <div className="metric-title">Categoría principal</div>
              </div>
            </div>
          </Col>

          <Col span={6}>
            <div className="marketplace-metric">
              <div className="metric-icon-wrapper metric-icon-average">
                <BarChartOutlined />
              </div>
              <div className="metric-content">
                <div className="metric-value">
                  {metrics.productsByCategory.length > 0
                    ? Math.round(metrics.totalProducts / metrics.productsByCategory.length)
                    : 0}
                </div>
                <div className="metric-title">Promedio por categoría</div>
              </div>
            </div>
          </Col>
        </Row>

        <div style={{ marginTop: 24 }}>
          <Title level={5}>Distribución por categoría (Top 10)</Title>
          <div style={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barChartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  angle={0}
                  textAnchor="middle"
                  height={40}
                  tickFormatter={(value) => truncateText(value, 12)}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                />
                <RechartsTooltip
                  content={<CustomTooltip />}
                />
                <Bar
                  dataKey="value"
                  fill="#1890ff"
                  radius={[2, 2, 0, 0]}
                >
                  {barChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={{ marginTop: 24 }}>
          <Collapse
            ghost
            size="small"
            style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
          >
            <Collapse.Panel
              header={
                <span>
                  <TableOutlined /> Ver todas las categorías
                </span>
              }
              key="1"
            >
              <Table
                dataSource={metrics.productsByCategory}
                columns={[
                  {
                    title: 'Categoría',
                    dataIndex: 'category',
                    key: 'category',
                    render: (text) => <Text strong>{text}</Text>
                  },
                  {
                    title: 'Productos',
                    dataIndex: 'count',
                    key: 'count',
                    render: (value) => (
                      <Tag color="blue">{value}</Tag>
                    )
                  },
                  {
                    title: 'Porcentaje',
                    key: 'percentage',
                    render: (_, record) => (
                      <Text type="secondary">
                        {metrics.totalProducts > 0
                          ? `${Math.round((record.count / metrics.totalProducts) * 100)}%`
                          : '0%'}
                      </Text>
                    )
                  }
                ]}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} categorías`
                }}
                size="small"
                style={{ marginTop: 8 }}
              />
            </Collapse.Panel>
          </Collapse>
        </div>
      </div>
    </Card>
  );
};

export default MarketplaceProductsAnalysis;